import React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";

const DEFAULT = "DEFAULT";
const HIGH = "HIGH";
const MAXRES = "MAXRES";
const MEDIUM = "MEDIUM";

// Helper function to create responsive background images (srcset-like behavior)
const createResponsiveBackgroundImage = (thumbnails) => {
  if (!thumbnails?.length) return {};

  // Sort thumbnails by quality/size (DEFAULT < MEDIUM < HIGH < MAXRES)
  const qualityOrder = { DEFAULT: 1, MEDIUM: 2, HIGH: 3, MAXRES: 4 };
  const sortedThumbnails = [...thumbnails].sort(
    (a, b) => qualityOrder[a.format] - qualityOrder[b.format]
  );

  // Get fallback image (best available)
  const fallbackThumbnail =
    thumbnails.find((t) => t.format === MAXRES) ||
    thumbnails.find((t) => t.format === HIGH) ||
    thumbnails.find((t) => t.format === MEDIUM) ||
    thumbnails.find((t) => t.format === DEFAULT);

  const styles = {};

  // Fallback for all browsers
  if (fallbackThumbnail) {
    styles.backgroundImage = `url('${fallbackThumbnail.url}')`;
  }

  // Modern browsers: CSS image-set() for different DPR (device pixel ratio)
  const imageSetUrls = sortedThumbnails.map(
    (thumb) => `url("${thumb.url}") 1x`
  );
  const webpImageSetUrls = sortedThumbnails
    .filter((thumb) => thumb.webpUrl)
    .map((thumb) => `url("${thumb.webpUrl}") 1x`);

  if (imageSetUrls.length > 0) {
    // Create image-set with different qualities for different screen densities
    const imageSetValue = imageSetUrls.join(", ");
    styles["-webkit-background-image"] = `-webkit-image-set(${imageSetValue})`;
    styles["background-image"] = `image-set(${imageSetValue})`;

    // WebP support with image-set
    if (webpImageSetUrls.length > 0) {
      const webpImageSetValue = webpImageSetUrls.join(", ");
      styles[
        '@supports (background-image: image-set(url("data:image/webp;base64,UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA==") 1x))'
      ] = {
        "-webkit-background-image": `-webkit-image-set(${webpImageSetValue})`,
        "background-image": `image-set(${webpImageSetValue})`,
      };
    }
  }

  // Media queries for different screen sizes (responsive srcset-like behavior)
  const defaultImg = thumbnails.find((t) => t.format === DEFAULT);
  const mediumImg = thumbnails.find((t) => t.format === MEDIUM);
  const highImg = thumbnails.find((t) => t.format === HIGH);
  const maxresImg = thumbnails.find((t) => t.format === MAXRES);

  // Small screens: use DEFAULT or MEDIUM
  if (defaultImg) {
    styles["@media (max-width: 480px)"] = {
      backgroundImage: `url('${defaultImg.url}')`,
      ...(defaultImg.webpUrl && {
        '@supports (background-image: url("data:image/webp;base64,UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA=="))':
          {
            backgroundImage: `url('${defaultImg.webpUrl}')`,
          },
      }),
    };
  }

  // Medium screens: use MEDIUM or HIGH
  if (mediumImg) {
    styles["@media (min-width: 481px) and (max-width: 768px)"] = {
      backgroundImage: `url('${mediumImg.url}')`,
      ...(mediumImg.webpUrl && {
        '@supports (background-image: url("data:image/webp;base64,UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA=="))':
          {
            backgroundImage: `url('${mediumImg.webpUrl}')`,
          },
      }),
    };
  }

  // Large screens: use HIGH
  if (highImg) {
    styles["@media (min-width: 769px) and (max-width: 1200px)"] = {
      backgroundImage: `url('${highImg.url}')`,
      ...(highImg.webpUrl && {
        '@supports (background-image: url("data:image/webp;base64,UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA=="))':
          {
            backgroundImage: `url('${highImg.webpUrl}')`,
          },
      }),
    };
  }

  // Extra large screens: use MAXRES
  if (maxresImg) {
    styles["@media (min-width: 1201px)"] = {
      backgroundImage: `url('${maxresImg.url}')`,
      ...(maxresImg.webpUrl && {
        '@supports (background-image: url("data:image/webp;base64,UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA=="))':
          {
            backgroundImage: `url('${maxresImg.webpUrl}')`,
          },
      }),
    };
  }

  return styles;
};

// Root container with 9:16 aspect ratio matching original shorty content
const Root = styled(Box, {
  shouldForwardProp: (prop) => !["verticalThumbnails"].includes(prop),
})(({ verticalThumbnails }) => {
  const baseStyles = {
    position: "relative",
    width: "100%",
    paddingTop: "177.78%", // 9:16 aspect ratio (16/9 * 100%) - как оригинальный контент
    overflow: "hidden",
    borderRadius: "inherit",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  };

  const responsiveStyles = createResponsiveBackgroundImage(verticalThumbnails);

  return {
    ...baseStyles,
    ...responsiveStyles,
  };
});

export default function ShortyThumbnailCardMedia({
  verticalThumbnails,
  children,
  ...props
}) {
  return (
    <Root verticalThumbnails={verticalThumbnails} {...props}>
      {children}
    </Root>
  );
}

ShortyThumbnailCardMedia.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  verticalThumbnails: PropTypes.arrayOf(
    PropTypes.shape({
      format: PropTypes.oneOf([DEFAULT, MEDIUM, HIGH, MAXRES]).isRequired,
      url: PropTypes.string.isRequired,
      webpUrl: PropTypes.string,
    })
  ),
};
